import { useCallback, useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { ExhMeasurementWbcResult } from "types/measurement";
import { PatientResult } from "types/patient";

import { convertExhWbcResultToPatientResult } from "../utils/measurementConverters";

interface UseExhWbcResultsResult {
  results: PatientResult[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches all measurement-wbc-result documents whose `device_measurement_id`
 * matches one of the patient's `device_measurement_ids`. Sorted newest first.
 */
export const useExhWbcResults = (deviceMeasurementIds: string[] | undefined): UseExhWbcResultsResult => {
  const [results, setResults] = useState<PatientResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { showBoundary: onError } = useErrorBoundary();

  // Memoize against the joined ids so callers passing fresh array references each render don't trigger refetches
  const idsKey = deviceMeasurementIds?.join(",") ?? "";
  const stableIds = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

  const load = useCallback(async () => {
    if (stableIds.length === 0) {
      setLoading(false);
      setResults([]);
      return;
    }

    if (!isExhClientInitialized()) {
      setLoading(false);
      setError(new Error("ExtraHorizon client not initialized"));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const client = getExhClient();
      const rql = rqlBuilder().in("data.device_measurement_id", stableIds).sort("-data.measurement_timestamp").build();
      const response = await client.data.documents.find("measurement-wbc-result", { rql });
      const docs = (response.data ?? []) as ExhMeasurementWbcResult[];
      setResults(docs.map(convertExhWbcResultToPatientResult));
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Failed to fetch measurement results");
      setError(e);
      onError(e);
    } finally {
      setLoading(false);
    }
  }, [stableIds, onError]);

  useEffect(() => {
    load();
  }, [load]);

  return { results, loading, error, refetch: load };
};
